import { Card, CardContent } from "@/components/ui/card";
import type { PageIssue, ProposedFix } from "@/types/technicalAudit";
import { PlainBeforeAfter } from "./PlainBeforeAfter";

interface GenericDiffCardProps {
	issue: PageIssue;
}

// A3: every issue type must show a before vs. after before approval. This is the
// fallback for types without a bespoke diff card — it renders the raw
// original_value -> proposed value so reviewers always see a clear change.
const extractProposed = (issue: PageIssue): string => {
	if (issue.proposed_value) return issue.proposed_value;
	const fix = issue.proposed_fix as ProposedFix | null;
	if (fix && typeof fix === "object") {
		const f = fix as Record<string, unknown>;
		const keys = [
			"new_value",
			"new_title",
			"new_h1",
			"new_alt_text",
			"new_meta_description",
			"recommended_target",
			"replacement_url",
			"recommendation",
			"value",
		];
		for (const k of keys) {
			if (typeof f[k] === "string" && (f[k] as string).trim()) {
				return f[k] as string;
			}
		}
		try {
			return JSON.stringify(fix, null, 2);
		} catch {
			/* ignore */
		}
	}
	return "";
};

export function GenericDiffCard({ issue }: GenericDiffCardProps) {
	const before = issue.original_value || "";
	const after = extractProposed(issue);

	return (
		<Card>
			<CardContent className="space-y-3 pt-4">
				{/* Always say what applying does, even for issue types without a
				    dedicated card — a bare before/after left users guessing (GH #599). */}
				<p className="text-xs text-muted-foreground">
					{after
						? "Applying writes the proposed value to WordPress, replacing the current one."
						: "Generate a fix to see the proposed value that will be written to WordPress."}
				</p>
				<PlainBeforeAfter
					before={
						before || <span className="italic">(empty / not set)</span>
					}
					after={
						after || (
							<span className="font-normal italic text-muted-foreground">
								Generate a fix to see the proposed value.
							</span>
						)
					}
					afterLabel="After (proposed)"
				/>
			</CardContent>
		</Card>
	);
}
